import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, DownloadSimple, Heart, Crown, Warning, Trash, User, LockKey, Plus, UserPlus, PencilSimple, Check, UserCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { creatorHref } from "../lib/links";
import { slugify } from "../lib/dropped";
import { useDownload } from "../context/DownloadContext";
import DownloadGateModal from "./DownloadGateModal";
import { useAuth } from "../context/AuthContext";
import BookmarkButton from "./BookmarkButton";
import SmartImg from "./SmartImg";
import UploadModal from "./upload/UploadModal";
import { cardFromMovie } from "./upload/MoviePicker";
import PersonPicker from "./PersonPicker";

/**
 * Side drawer for one character: opened with any scenepack id, it shows the
 * character's poster and every version (scenepack) of that character as a
 * list, each with its own download button.
 * On every fresh open we surface a slowly-flashing credit warning that the
 * editor must acknowledge before the list + download buttons become visible.
 *
 * `onChanged` fires after a scenepack is deleted so the parent can refresh.
 */
export default function PackDrawer({ packId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ackedCredit, setAckedCredit] = useState(false);
  const isAdmin = (user?.roles || []).includes("admin");
  const { requestDownload, gate, downloadFromGate, closeGate } = useDownload();
  const unlimited = !!user?.has_aa_perks; // AA+ members, admins and scenepack creators
  const [quota, setQuota] = useState(null); // { limit, remaining } for free accounts
  const [adminUpload, setAdminUpload] = useState(null); // { card, lockCharacter } — an admin adding to this movie
  const [openingUpload, setOpeningUpload] = useState(false);
  const [editing, setEditing] = useState(null); // { id, value, saving } — renaming one version
  const [pickingActor, setPickingActor] = useState(false); // the "who plays them?" window
  const [cast, setCast] = useState([]); // IMDb's cast list for this movie, offered as suggestions there
  const [opened, setOpened] = useState(null); // the pack that was clicked: its own image is the drawer's picture

  useEffect(() => {
    if (!packId || !open) return undefined;
    let cancelled = false;
    setLoading(true);
    setVersions([]);
    setOpened(null);
    setAckedCredit(false); // every fresh open re-shows the warning
    api
      .get(`/scenepacks/${packId}`)
      .then(async (r) => {
        const lead = r.data;
        // Matched by character_slug (computed the same way everywhere, so it
        // isn't thrown off by a stray space or different casing between two
        // versions' character names) rather than an exact-text match on
        // movie_title/character_name, which used to silently hide versions
        // whose name wasn't byte-for-byte identical to this one's.
        const movie = await api.get(`/movies/${lead.movie_slug || slugify(lead.movie_title)}`).catch(() => null);
        const siblings = movie?.data?.packs?.filter((p) => p.character_slug === lead.character_slug) || [];
        if (!cancelled) { setOpened(lead); setVersions(siblings.length ? siblings : [lead]); }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [packId, open]);

  useEffect(() => {
    if (!open || unlimited) return;
    api.get("/scenepacks/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, [open, unlimited]);

  const replaceVersion = (next) => setVersions((vs) => vs.map((v) => (v.id === next.id ? next : v)));

  const handleLike = async (v) => {
    if (!user) return;
    const { data } = await api.post(`/scenepacks/${v.id}/like`);
    replaceVersion(data);
  };

  const handleDownload = async (v) => {
    const res = await requestDownload(v, { owner: "drawer" });
    if (!res) return;
    replaceVersion({ ...v, downloads: v.downloads + 1 });
    if (res.remaining !== null && res.remaining !== undefined) setQuota((q) => ({ ...(q || {}), limit: res.limit, remaining: res.remaining }));
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? It goes to the trash for 7 days, and an admin can restore it.`)) return;
    try {
      await api.delete(`/scenepacks/${v.id}`);
      toast.success("Scenepack deleted");
      const remaining = versions.filter((x) => x.id !== v.id);
      setVersions(remaining);
      onChanged?.();
      if (!remaining.length) onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const saveName = async () => {
    if (!editing || editing.saving) return;
    const v = versions.find((x) => x.id === editing.id);
    const name = editing.value.replace(/\s+/g, " ").trim();
    if (!v || name === v.title) { setEditing(null); return; }
    if (!name) { toast.error("The name can't be empty"); return; }
    setEditing({ ...editing, saving: true });
    try {
      const { data } = await api.patch(`/scenepacks/${v.id}`, { title: name });
      replaceVersion({ ...v, title: data.title });
      toast.success("Name updated");
      setEditing(null);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not rename");
      setEditing((e) => (e ? { ...e, saving: false } : e));
    }
  };

  // Who plays this character: every version of it gets the actor (person = null removes the link).
  const setActor = async (person) => {
    const lead0 = versions[0];
    if (!lead0) return;
    try {
      await api.put(`/movies/${lead0.movie_slug || slugify(lead0.movie_title)}/characters/${lead0.character_slug}/person`, { person_id: person?.id || "" });
      setVersions((vs) => vs.map((v) => ({ ...v, person_id: person?.id || "", person: person ? { id: person.id, slug: person.slug, name: person.name, photo_url: person.photo_url } : null })));
      toast.success(person ? `${person.name} is linked to ${lead0.character_name}` : "Actor removed");
      setPickingActor(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not link the actor");
    }
  };

  // Admins: add a version to this character (lock = true) or a new character to this movie.
  // The drawer closes first; the upload window then opens over the page.
  const startAdminUpload = async (lock) => {
    if (!versions[0] || openingUpload) return;
    setOpeningUpload(true);
    try {
      const { data } = await api.get(`/movies/${versions[0].movie_slug || slugify(versions[0].movie_title)}`);
      setAdminUpload({ card: cardFromMovie(data), lockCharacter: lock ? (versions[0].character_name || "").trim() : "" });
      onOpenChange(false);
    } catch {
      toast.error("Couldn't open the upload window");
    }
    setOpeningUpload(false);
  };

  const lead = versions[0];
  useEffect(() => {
    if (!pickingActor || !lead) return;
    api.get(`/movies/${lead.movie_slug || slugify(lead.movie_title)}/cast`).then((r) => setCast(r.data.items || [])).catch(() => setCast([]));
    // eslint-disable-next-line
  }, [pickingActor]);
  const canSetActor = !!lead && lead.franchise !== "celebrities" && (isAdmin || (!!user && versions.some((v) => v.creator_id === user.id)));
  const qualities = Array.from(new Set(versions.map((v) => v.quality).filter(Boolean)));
  const heroImage = opened?.thumbnail_url || lead?.thumbnail_url;

  return (
    <>
    {adminUpload && (
      <UploadModal
        card={adminUpload.card}
        lockCharacter={adminUpload.lockCharacter}
        title={adminUpload.lockCharacter ? `Add a version — ${adminUpload.lockCharacter}` : `Add a character — ${adminUpload.card.title}`}
        onClose={() => setAdminUpload(null)}
        onDone={() => { setAdminUpload(null); onChanged?.(); }}
      />
    )}
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
          data-testid="pack-drawer"
          // Escape closes the sponsor window / the rename box first, not the drawer behind it
          onEscapeKeyDown={(e) => {
            if (gate?.owner === "drawer") e.preventDefault();
            else if (editing) { e.preventDefault(); if (!editing.saving) setEditing(null); }
          }}
          className="fixed top-0 right-0 z-[80] h-full w-full sm:w-[480px] lg:w-[560px] bg-[#121212] border-l border-white/15 shadow-2xl flex flex-col
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right
            duration-300"
        >
          <Dialog.Title className="sr-only">{lead?.character_name || "Scenepack details"}</Dialog.Title>
          <Dialog.Description className="sr-only">Every scenepack version for this character</Dialog.Description>

          <Dialog.Close
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/15 hover:bg-[#880015] hover:border-[#880015] text-white flex items-center justify-center transition"
            data-testid="pack-drawer-close"
          >
            <X size={18} weight="bold" />
          </Dialog.Close>

          {loading && (
            <div className="flex-1 flex items-center justify-center text-white/40 font-mono text-xs tracking-[0.3em]">LOADING…</div>
          )}

          {lead && !ackedCredit && (
            <div
              data-testid="pack-credit-warning"
              className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gradient-to-b from-[#0F0204] via-[#121212] to-[#0F0204] credit-warning-flash"
            >
              <div className="w-16 h-16 rounded-full bg-[#880015]/15 border border-[#FF8090]/40 flex items-center justify-center mb-5">
                <Warning size={32} weight="fill" className="text-[#FF8090]" />
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-[#FF8090] mb-2">Hold up</div>
              <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-3 leading-tight">
                Remember to credit<br /><span className="text-[#FF8090]">@alphascps</span>
              </h2>
              <p className="text-sm text-white/60 max-w-xs mb-7">
                If you use this scenepack in an edit, tag <span className="text-white font-bold">@alphascps</span> in your video description. It's the only thing we ask.
              </p>
              <button
                type="button"
                onClick={() => setAckedCredit(true)}
                data-testid="pack-credit-ack"
                className="font-display font-bold text-xs uppercase tracking-[0.2em] px-7 py-3 rounded-md bg-[#880015] hover:bg-[#A6001A] text-white shadow-[0_0_24px_rgba(136,0,21,0.4)] transition"
              >
                Got it — show me the pack
              </button>
            </div>
          )}

          {lead && ackedCredit && (
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {/* Hero image */}
              <div className="relative aspect-[16/10] overflow-hidden bg-[#1A1A1A]">
                {heroImage && <SmartImg src={heroImage} width={960} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {qualities.map((q) => <span key={q} className="chip chip-garnet">{q}</span>)}
                    {lead.genre && <span className="chip">{lead.genre}</span>}
                  </div>
                  {lead.franchise === "celebrities" ? (
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-1">Celebrity</div>
                  ) : (
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/60 mb-1">
                      {lead.movie_title} {lead.year ? `· ${lead.year}` : ""}
                    </div>
                  )}
                  <h2 className="font-display font-black text-3xl tracking-tighter leading-tight" data-testid="drawer-character-name">{lead.character_name}</h2>
                  {lead.franchise !== "celebrities" && (lead.person || canSetActor) && (
                    <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid="drawer-actor">
                      {lead.person ? (
                        <Link to={`/people/${lead.person.slug}`} onClick={() => onOpenChange(false)} data-testid="drawer-actor-link"
                          className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-black/55 backdrop-blur-md border border-white/15 hover:border-[#FF8090]/60 transition">
                          <span className="w-6 h-6 rounded-full overflow-hidden bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                            {lead.person.photo_url ? <img src={lead.person.photo_url} alt="" className="w-full h-full object-cover" /> : <UserCircle size={16} className="text-white/40" />}
                          </span>
                          <span className="text-[11px] text-white/60">Played by</span>
                          <span className="text-xs font-display font-bold">{lead.person.name}</span>
                        </Link>
                      ) : null}
                      {canSetActor && (
                        <button type="button" onClick={() => setPickingActor(true)} data-testid="drawer-set-actor"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 bg-black/40 text-[11px] font-display font-bold text-white/70 hover:text-white hover:border-white/40 transition">
                          <UserCircle size={13} /> {lead.person ? "Change actor" : "Who plays them?"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Versions list */}
              <div className="p-6 space-y-6">
                <div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090]">
                      Scenepacks · {versions.length}
                    </div>
                    {!user && (
                      <span className="text-[10px] text-white/40 font-mono">
                        <Link to="/login" className="text-[#FF8090] underline" onClick={() => onOpenChange(false)}>Sign in</Link> to like
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="mb-4 flex gap-2" data-testid="drawer-admin-actions">
                      <button type="button" onClick={() => startAdminUpload(true)} disabled={openingUpload} data-testid="drawer-add-version"
                        className="flex-1 flex items-center justify-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wider px-3 py-2.5 rounded-lg border border-[#880015]/60 bg-[#880015]/10 text-[#FF8090] hover:bg-[#880015] hover:text-white transition disabled:opacity-60">
                        <Plus size={13} weight="bold" /> Add version
                      </button>
                      <button type="button" onClick={() => startAdminUpload(false)} disabled={openingUpload} data-testid="drawer-add-character"
                        className="flex-1 flex items-center justify-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wider px-3 py-2.5 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/35 transition disabled:opacity-60">
                        <UserPlus size={13} /> New character
                      </button>
                    </div>
                  )}
                  <div className="mb-3 text-[11px] font-mono text-white/45" data-testid="drawer-quota">
                    {unlimited ? (
                      <span className="inline-flex items-center gap-1.5 text-[#FF8090]"><Crown size={11} weight="fill" /> AA+ — unlimited downloads</span>
                    ) : quota ? (
                      <span>
                        {quota.remaining} of {quota.limit} free downloads left today ·{" "}
                        <Link to="/aa-plus" onClick={() => onOpenChange(false)} className="text-[#FF8090] underline">Go unlimited</Link>
                      </span>
                    ) : null}
                  </div>
                  <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/5 bg-[#161616]" data-testid="drawer-versions">
                    {versions.map((v) => {
                      const canDelete = isAdmin || user?.id === v.creator_id;
                      return (
                        <div key={v.id} className="p-4" data-testid={`drawer-version-${v.id}`}>
                          {editing?.id === v.id && (
                            <form onSubmit={(e) => { e.preventDefault(); saveName(); }} className="flex items-center gap-1.5 mb-3" data-testid={`drawer-rename-form-${v.id}`}>
                                <input
                                  autoFocus
                                  value={editing.value}
                                  maxLength={100}
                                  onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                  disabled={editing.saving}
                                  aria-label="Scenepack name"
                                  data-testid={`drawer-rename-input-${v.id}`}
                                  className="min-w-0 flex-1 bg-black/40 border border-white/20 focus:border-[#880015] outline-none rounded-md px-2.5 py-1.5 text-base sm:text-[14px] font-display font-bold"
                                />
                                <button type="submit" disabled={editing.saving} title="Save name" data-testid={`drawer-rename-save-${v.id}`}
                                  className="w-8 h-8 rounded-md bg-[#880015] hover:bg-[#a0001a] text-white flex items-center justify-center flex-shrink-0 transition disabled:opacity-60"><Check size={14} weight="bold" /></button>
                                <button type="button" onClick={() => setEditing(null)} disabled={editing.saving} title="Cancel" data-testid={`drawer-rename-cancel-${v.id}`}
                                  className="w-8 h-8 rounded-md border border-white/15 text-white/60 hover:text-white flex items-center justify-center flex-shrink-0 transition"><X size={13} /></button>
                              </form>
                          )}
                          <div className="flex items-start gap-3">
                            <div className="min-w-0 flex-1">
                              {editing?.id !== v.id && (
                                <div className="flex items-center gap-2">
                                  <div className="font-display font-bold text-[15px] leading-snug break-words min-w-0">{v.title}</div>
                                  {canDelete && (
                                    <button type="button" onClick={() => setEditing({ id: v.id, value: v.title, saving: false })} title="Rename scenepack" aria-label="Rename scenepack" data-testid={`drawer-rename-${v.id}`}
                                      className="w-6 h-6 rounded-md text-white/35 hover:text-white hover:bg-white/10 flex items-center justify-center flex-shrink-0 transition"><PencilSimple size={12} /></button>
                                  )}
                                </div>
                              )}
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-white/50">
                                <span className="chip chip-garnet !py-0.5">{v.quality}</span>
                                {v.premium && <span className="chip chip-garnet !py-0.5 flex items-center gap-1"><Crown size={9} weight="fill" /> AA+</span>}
                                {v.creator_name && (
                                  <Link to={creatorHref(v)} onClick={() => onOpenChange(false)} className="flex items-center gap-1 hover:text-white transition">
                                    <User size={11} /> {v.creator_name}
                                  </Link>
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-2.5 text-xs font-mono text-white/45">
                                <span>{v.file_size || "—"}</span>
                                <span className="flex items-center gap-1 text-white/70"><DownloadSimple size={12} /> {v.downloads.toLocaleString()}</span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              {v.premium && !unlimited ? (
                                <Link
                                  to="/aa-plus"
                                  onClick={() => onOpenChange(false)}
                                  data-testid={`drawer-unlock-${v.id}`}
                                  className="btn-secondary !py-2 !px-4 !text-xs"
                                >
                                  <LockKey size={14} /> AA+ only
                                </Link>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleDownload(v)}
                                  data-testid={`drawer-download-${v.id}`}
                                  className="btn-primary !py-2 !px-4 !text-xs"
                                >
                                  <DownloadSimple size={14} /> Download
                                </button>
                              )}
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleLike(v)}
                                  disabled={!user}
                                  data-testid={`drawer-like-${v.id}`}
                                  className={`flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md border border-white/10 hover:border-white/30 transition disabled:opacity-40 ${
                                    v.liked_by_me ? "!border-[#880015] text-[#FF4060]" : "text-white/60"
                                  }`}
                                >
                                  <Heart size={12} weight={v.liked_by_me ? "fill" : "regular"} /> {v.likes}
                                </button>
                                <BookmarkButton kind="scenepack" id={v.id} testId={`drawer-bookmark-${v.id}`} />
                                {canDelete && (
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(v)}
                                    data-testid={`drawer-delete-${v.id}`}
                                    title={isAdmin ? "Admin: delete scenepack" : "Delete scenepack"}
                                    className="w-7 h-7 rounded-md border border-white/10 hover:border-[#880015] hover:bg-[#880015] text-white/50 hover:text-white flex items-center justify-center transition"
                                  >
                                    <Trash size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Synopsis — movie-level IMDb/RT live on the movie page */}
                {lead.movie_synopsis && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-2">About the movie</div>
                    <p className="text-sm text-white/70 leading-relaxed">{lead.movie_synopsis}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {pickingActor && lead && (
            <div className="absolute inset-0 z-20 bg-[#121212]/95 backdrop-blur-sm flex flex-col p-6 overflow-y-auto" data-testid="actor-picker-panel" style={{ pointerEvents: "auto" }}>
              <div className="flex items-start justify-between gap-4 mb-5 pr-12">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-1">{lead.movie_title}</div>
                  <h3 className="font-display font-black text-2xl leading-tight">Who plays {lead.character_name}?</h3>
                </div>
              </div>
              <PersonPicker onPick={setActor} selectedId={lead.person_id} suggestions={cast} suggestionsTitle={`In the cast of ${lead.movie_title}`} autoFocus testId="actor-picker" />
              <div className="mt-4 flex items-center gap-3">
                <button type="button" onClick={() => setPickingActor(false)} className="btn-secondary !py-2 !px-4 !text-xs">Cancel</button>
                {lead.person && <button type="button" onClick={() => setActor(null)} className="text-xs text-white/50 hover:text-[#FF4060] transition">Remove the actor</button>}
              </div>
            </div>
          )}
          {gate?.owner === "drawer" && <DownloadGateModal gate={gate} onDownload={downloadFromGate} onClose={closeGate} portal={false} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
    </>
  );
}
